import type { CSSProperties } from 'react';

import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

import type { CustomAttribute } from '@/types';
import { cn } from '@/utils/cn';

interface TagFilterAttributes {
  borderRadius?: CSSProperties['borderRadius'];
  borderWidth?: CSSProperties['borderWidth'];
  borderStyle?: CSSProperties['borderStyle'];
  borderColor?: CSSProperties['borderColor'];
  textColor?: CSSProperties['color'];
  backgroundColor?: CSSProperties['backgroundColor'];
  activeBackgroundColor?: CSSProperties['backgroundColor'];
  activeTextColor?: CSSProperties['color'];
}

interface TagFilterProps {
  tags: Array<{ id: string; display: string }>;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  attributes: TagFilterAttributes;
  customAttributes?: CustomAttribute;
  contentTagsMatch?: 'any' | 'all';
  onContentTagsMatchChange?: (match: 'any' | 'all') => void;
}

const TagFilter = ({
  tags,
  selectedTags,
  onTagsChange,
  attributes,
  customAttributes,
  contentTagsMatch = 'any',
  onContentTagsMatchChange,
}: TagFilterProps) => {
  const handleTagClick = (tagDisplay: string) => {
    const isSelected = selectedTags.includes(tagDisplay);

    if (isSelected) {
      // Remove tag
      onTagsChange(selectedTags.filter((t) => t !== tagDisplay));
    } else {
      // Add tag
      onTagsChange([...selectedTags, tagDisplay]);
    }
  };

  const handleClearFilters = () => {
    onTagsChange([]);
  };

  if (!tags || tags.length === 0) {
    return null;
  }

  const hasSelectedTags = selectedTags.length > 0;
  const hasMultipleTags = selectedTags.length > 1;

  return (
    <div
      className={cn('flex flex-col gap-2 w-full', customAttributes?.className)}
      style={customAttributes?.style || {}}
    >
      {/* Tag buttons */}
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => {
          const isSelected = selectedTags.includes(tag.display);

          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => handleTagClick(tag.display)}
              className="cursor-pointer px-4 py-1.5 text-xs transition-colors duration-150"
              style={{
                borderRadius: attributes.borderRadius,
                borderWidth: attributes.borderWidth,
                borderStyle: attributes.borderStyle,
                borderColor: attributes.borderColor,
                color: isSelected ? attributes.activeTextColor : attributes.textColor,
                backgroundColor: isSelected ? attributes.activeBackgroundColor : attributes.backgroundColor,
              }}
            >
              {tag.display}
            </button>
          );
        })}
      </div>

      {/* Clear filters and match mode toggle */}
      {hasSelectedTags && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleClearFilters}
            className="flex items-center gap-1 cursor-pointer px-2 py-1 text-xs w-fit transition-colors duration-150"
            style={{
              borderRadius: attributes.borderRadius,
              borderWidth: attributes.borderWidth,
              borderStyle: attributes.borderStyle,
              borderColor: attributes.borderColor,
              color: attributes.textColor,
              backgroundColor: 'transparent',
            }}
          >
            <XMarkIcon className="h-3 w-3" />
            Clear filters
          </button>
          {hasMultipleTags && onContentTagsMatchChange && (
            <>
              <span className="text-xs" style={{ color: attributes.textColor }}>
                Match:
              </span>
              <div
                className="inline-flex overflow-hidden"
                style={{
                  borderRadius: attributes.borderRadius,
                  borderWidth: attributes.borderWidth,
                  borderStyle: attributes.borderStyle,
                  borderColor: attributes.borderColor,
                }}
              >
                <button
                  type="button"
                  onClick={() => onContentTagsMatchChange('any')}
                  className="cursor-pointer px-3 py-1 text-xs font-medium transition-colors duration-150"
                  style={{
                    color: contentTagsMatch === 'any' ? attributes.activeTextColor : attributes.textColor,
                    backgroundColor:
                      contentTagsMatch === 'any' ? attributes.activeBackgroundColor : attributes.backgroundColor,
                  }}
                >
                  Any tag
                </button>
                <button
                  type="button"
                  onClick={() => onContentTagsMatchChange('all')}
                  className="cursor-pointer px-3 py-1 text-xs font-medium transition-colors duration-150"
                  style={{
                    color: contentTagsMatch === 'all' ? attributes.activeTextColor : attributes.textColor,
                    backgroundColor:
                      contentTagsMatch === 'all' ? attributes.activeBackgroundColor : attributes.backgroundColor,
                    borderLeft: `${attributes.borderWidth} ${attributes.borderStyle} ${attributes.borderColor}`,
                  }}
                >
                  All tags
                </button>
              </div>
              <div className="relative group">
                <button type="button" className="cursor-help" aria-label="Tag matching mode explanation">
                  <InformationCircleIcon className="h-4 w-4" style={{ color: attributes.textColor }} />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block group-focus-within:block z-10">
                  <div className="whitespace-nowrap rounded bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg text-left leading-5">
                    <strong>Any tag</strong> — posts matching at least one selected tag
                    <br />
                    <strong>All tags</strong> — posts matching every selected tag
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TagFilter;
